import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAvailableModels } from '@/hooks/useAvailableModels';
import { FlaskConical, Sliders, Palette, Settings2, Trash2, Plus } from 'lucide-react';
import { toast } from 'sonner';

interface TestConfig {
  name: string;
  description: string;
  basePrompt: string;
  testType: 'model_comparison' | 'parameter_testing' | 'style_variation' | 'custom';
  variants: Array<{
    name: string;
    model?: string;
    size?: string;
    quality?: string;
    style?: string;
  }>;
}

interface TestConfigWizardProps {
  onComplete: (config: TestConfig) => void;
  onCancel: () => void;
}

export const TestConfigWizard = ({ onComplete, onCancel }: TestConfigWizardProps) => {
  const { models } = useAvailableModels();
  const [step, setStep] = useState(1);
  const [config, setConfig] = useState<TestConfig>({
    name: '',
    description: '',
    basePrompt: '',
    testType: 'model_comparison',
    variants: []
  });

  const testTypes = [
    {
      id: 'model_comparison' as const,
      name: 'Model Comparison',
      icon: FlaskConical,
      description: 'Compare different AI models with the same prompt'
    },
    {
      id: 'parameter_testing' as const,
      name: 'Parameter Testing',
      icon: Sliders,
      description: 'Test different sizes, qualities, or settings'
    },
    {
      id: 'style_variation' as const,
      name: 'Style Variation',
      icon: Palette,
      description: 'Compare different artistic styles'
    },
    {
      id: 'custom' as const,
      name: 'Custom Test',
      icon: Settings2,
      description: 'Full flexibility for advanced testing'
    }
  ];

  const handleTestTypeSelect = (testType: TestConfig['testType']) => {
    // Auto-generate variants based on test type
    if (testType === 'model_comparison') {
      const topModels = models.slice(0, 3);
      setConfig(prev => ({
        ...prev,
        testType,
        variants: topModels.map((m, i) => ({
          name: `Variant ${i + 1}: ${m.display_name}`,
          model: m.model_key,
          size: '1024x1024',
          quality: 'standard'
        }))
      }));
    } else if (testType === 'parameter_testing') {
      setConfig(prev => ({
        ...prev,
        testType,
        variants: [
          { name: 'Square (1024x1024)', size: '1024x1024', quality: 'standard' },
          { name: 'Landscape (1792x1024)', size: '1792x1024', quality: 'standard' },
          { name: 'Portrait (1024x1792)', size: '1024x1792', quality: 'standard' }
        ]
      }));
    } else if (testType === 'style_variation') {
      setConfig(prev => ({
        ...prev,
        testType,
        variants: [
          { name: 'Modern Minimalist', style: 'modern minimalist clean design' },
          { name: 'Vintage Retro', style: 'vintage retro nostalgic aesthetic' },
          { name: 'Futuristic Sci-Fi', style: 'futuristic sci-fi cyberpunk neon' }
        ]
      }));
    } else {
      // For custom test type, just set the type without auto-generating variants
      setConfig(prev => ({
        ...prev,
        testType,
        variants: []
      }));
    }
  };
  
  // Auto-populate variants on first load if empty, based on selected test type
  useEffect(() => {
    if (models.length > 0 && config.variants.length === 0) {
      handleTestTypeSelect(config.testType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [models]);
  
  const canProceedToStep2 = config.name && config.basePrompt && config.testType;
  const canComplete = config.variants.length >= 2;

  const handleRemoveVariant = (index: number) => {
    setConfig(prev => ({
      ...prev,
      variants: prev.variants.filter((_, i) => i !== index)
    }));
    toast.success('Variant removed');
  };

  const handleAddVariant = () => {
    const newVariant = {
      name: `Variant ${config.variants.length + 1}`,
      model: models[0]?.model_key,
      size: '1024x1024',
      quality: 'standard'
    };
    setConfig(prev => ({
      ...prev,
      variants: [...prev.variants, newVariant]
    }));
    toast.success('Variant added');
  };

  return (
    <div className="space-y-6">
      {/* Progress Steps */}
      <div className="flex items-center justify-center space-x-4 mb-8">
        <div className={`flex items-center space-x-2 ${step >= 1 ? 'text-blue-400' : 'text-gray-500'}`}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-blue-600' : 'bg-gray-700'}`}>
            1
          </div>
          <span className="text-sm font-medium">Basic Info</span>
        </div>
        <div className="w-16 h-0.5 bg-gray-700"></div>
        <div className={`flex items-center space-x-2 ${step >= 2 ? 'text-blue-400' : 'text-gray-500'}`}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-blue-600' : 'bg-gray-700'}`}>
            2
          </div>
          <span className="text-sm font-medium">Configure Test</span>
        </div>
      </div>

      {/* Step 1: Basic Information */}
      {step === 1 && (
        <div className="space-y-6">
          <div>
            <Label htmlFor="test-name" className="text-white">Test Name *</Label>
            <Input
              id="test-name"
              placeholder="e.g., Product Photo Comparison"
              value={config.name}
              onChange={(e) => setConfig({ ...config, name: e.target.value })}
              className="mt-2 bg-white/5 border-white/10 text-white"
            />
          </div>

          <div>
            <Label htmlFor="test-description" className="text-white">Description (Optional)</Label>
            <Textarea
              id="test-description"
              placeholder="What are you trying to test?"
              value={config.description}
              onChange={(e) => setConfig({ ...config, description: e.target.value })}
              className="mt-2 bg-white/5 border-white/10 text-white"
              rows={3}
            />
          </div>

          <div>
            <Label htmlFor="base-prompt" className="text-white">Base Prompt *</Label>
            <Textarea
              id="base-prompt"
              placeholder="Enter the prompt you want to test..."
              value={config.basePrompt}
              onChange={(e) => setConfig({ ...config, basePrompt: e.target.value })}
              className="mt-2 bg-white/5 border-white/10 text-white"
              rows={4}
            />
          </div>

          <div>
            <Label className="text-white mb-3 block">Test Type *</Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {testTypes.map((type) => (
                <Card
                  key={type.id}
                  className={`p-4 cursor-pointer transition-all hover:scale-105 ${
                    config.testType === type.id
                      ? 'bg-blue-600/20 border-blue-500'
                      : 'bg-white/5 border-white/10 hover:bg-white/10'
                  }`}
                  onClick={() => handleTestTypeSelect(type.id)}
                >
                  <div className="flex items-start space-x-3">
                    <type.icon className="w-6 h-6 text-blue-400 flex-shrink-0" />
                    <div>
                      <h3 className="text-white font-semibold mb-1">{type.name}</h3>
                      <p className="text-gray-400 text-sm">{type.description}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (config.variants.length === 0) {
                  handleTestTypeSelect(config.testType);
                }
                setStep(2);
              }}
              disabled={!canProceedToStep2}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Next: Configure Variants
            </Button>
          </div>
        </div>
      )}

      {/* Step 2: Configure Variants */}
      {step === 2 && (
        <div className="space-y-6">
          <div className="bg-white/5 rounded-lg p-4 border border-white/10">
            <h3 className="text-white font-semibold mb-2">Test Configuration</h3>
            <div className="space-y-2 text-sm">
              <p className="text-gray-400">
                <span className="text-white font-medium">Name:</span> {config.name}
              </p>
              <p className="text-gray-400">
                <span className="text-white font-medium">Type:</span> {testTypes.find(t => t.id === config.testType)?.name}
              </p>
              <p className="text-gray-400">
                <span className="text-white font-medium">Prompt:</span> {config.basePrompt.substring(0, 100)}...
              </p>
            </div>
          </div>

          <div>
            <div className="flex justify-between items-center mb-3">
              <Label className="text-white">
                Variants ({config.variants.length})
                <Badge className="ml-2 bg-blue-600">{canComplete ? 'Ready' : 'Need 2+ variants'}</Badge>
              </Label>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={handleAddVariant}
                className="text-white border-white/20 hover:bg-white/10"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Variant
              </Button>
            </div>
            <div className="space-y-3">
              {config.variants.length === 0 ? (
                <Card className="p-8 bg-white/5 border-white/10 border-dashed">
                  <div className="text-center">
                    <p className="text-gray-400 mb-4">No variants yet. Add at least 2 variants to continue.</p>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleAddVariant}
                      className="text-white border-white/20 hover:bg-white/10"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Your First Variant
                    </Button>
                  </div>
                </Card>
              ) : (
                config.variants.map((variant, index) => (
                  <Card key={index} className="p-4 bg-white/5 border-white/10 hover:border-white/20 transition-colors">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h4 className="text-white font-medium mb-2">{variant.name}</h4>
                        <div className="flex flex-wrap gap-2">
                          {variant.model && <Badge variant="outline">{variant.model}</Badge>}
                          {variant.size && <Badge variant="outline">{variant.size}</Badge>}
                          {variant.quality && <Badge variant="outline">{variant.quality}</Badge>}
                          {variant.style && <Badge variant="outline">{variant.style}</Badge>}
                        </div>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => handleRemoveVariant(index)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10 ml-2"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </Card>
                ))
              )}
            </div>
          </div>

          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep(1)}>
              Back
            </Button>
            <Button
              onClick={() => onComplete(config)}
              disabled={!canComplete}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Create Test & Generate Images
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
